import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { catchError, map, of, switchMap } from 'rxjs';
import { RestaurantService } from '../services/restaurant.service';
import { ReservationService } from '../services/reservation.service';
import { FEATURE_FLAG_KEYS, isFeatureActive } from '../utils/feature-flag.util';

/**
 * Gates the Reservations pages on the platform `RESERVATIONS` feature flag
 * (rollout / kill switch) and the restaurant's own reservation mode:
 *
 * - flag off, or the flags can't be loaded → both pages redirect to the dashboard
 * - mode `off` → the book (`reservation`) redirects to `reservation-settings`,
 *   which stays reachable so the restaurant can switch reservations on
 *
 * A failed settings read lets the book through; the page shows its own error.
 */
export const ReservationGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const router = inject(Router);
  const restaurantService = inject(RestaurantService);
  const reservationService = inject(ReservationService);

  return restaurantService.loadFeatureFlags().pipe(
    switchMap((flags) => {
      if (!isFeatureActive(flags, FEATURE_FLAG_KEYS.RESERVATIONS)) {
        return of(router.createUrlTree(['main', 'dashboard']));
      }
      if (route.routeConfig?.path !== 'reservation') {
        return of(true);
      }
      return reservationService.loadMode().pipe(
        map((mode) =>
          mode === 'off' ? router.createUrlTree(['main', 'reservation-settings']) : true,
        ),
        catchError(() => of(true)),
      );
    }),
  );
};
